import { Tooltip } from "@mui/material";
import Paper from "@mui/material/Paper";
import { ReactComponent as RecurringIcon } from "assets/icons/clockWise_refresh.svg";
import PaymentInstallment from "components/PaymentInstallment/PaymentInstallment";
import { convertUTCToLocalDate } from "components/UTC/UtcConverter";
import * as calculation from "helper/Calculate";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import FormService from "Lib/API/Forms";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formSlice } from "services/api/forms";
import { getAccessTokenSilently, getAuthenticated } from "utils/functions";
import {
  DataFoundWrapper,
  DiscountAmount,
  DiscountLabel,
  DiscountRow,
  PayDetails,
  PaymentAmount,
  SpinnerIcon,
  SummaryItem,
  SummaryItemCharge,
  SummaryItemInfo,
  SummaryItemName,
  SummaryTitle,
  TotalDueAmount,
  TotalPayment,
  Wrapper
} from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";

export const SummaryCard = ({
  handleTotalForPay,
  totalPaymentWithCVFee,
  billMyAccount,
  newProducts,
  setProducts,
  dueAmountCF,
  totalDueAmountWithCF,
  totalDiscount,
  dueAmountTotalDiscount,
  totalDueAmount,
  isInstallmentEnabled,
  setActivePaymentAfter,
  activePayment,
  setConvenienceFeeByPayment,
  convenienceFeeByPayment,
  currentPaymentMethod,
  onCalcPriceStatusChange,
  onCalcDueAmountChange,
  donationAmount,
}) => {
  const {
    products,
    formOnlineData,
    varients,
    numberOfTickets,
    totalProducts
  } = useSelector(state => state?.Forms);
  const totalPay = handleTotalForPay();
  const isAuthenticated = getAuthenticated();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [fetchingDiscount, setFetchingDiscount] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [calcResult, setCalcResult] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);

  const triggerRefetch = () => setRefetchTrigger((t) => t + 1);

  useEffect(() => {
    if (!formOnlineData?._id) return;
    let cancelled = false;
    const fetchCalcPrice = async () => {
      setFetchingDiscount(true);
      try {
        const token =
          (await getAccessTokenSilently()) ||
          localStorage.getItem("schoolToken") ||
          localStorage.getItem("formToken");
        if (!token || cancelled) return;

        const prices = formOnlineData?.formPricing?.prices || [];
        const installments = [];
        prices.forEach((p) => {
          if (p && p.installmentStatus && p.installment && p.installment.value) {
            installments.push({ priceId: p._id, numberOfInstallment: p.installment.value });
          }
        });

        const buildProducts = () =>
          (newProducts || [])
            .filter((p) => p && p.total > 0)
            .map((p) => {
              const obj = { id: p.priceId, title: p.title, quantity: p.total };
              const varientItem = varients && Object.values(varients).find((v) => v.id === p.priceId);
              if (p.type === "openAmount" && varientItem?.price > 0) {
                obj.price = varientItem.price;
              }
              if (varientItem?.variantsInfo?.length) {
                const variant = {};
                varientItem.variantsInfo.forEach((v) => {
                  if (v.counterValue > 0) {
                    const isOpenAmt = v.openAmount || varientItem?.type === "openAmount";
                    variant[v.option] = isOpenAmt
                      ? { price: v.price ?? v.amount ?? 0, quantity: v.counterValue }
                      : v.counterValue;
                  }
                });
                if (Object.keys(variant).length > 0) obj.variant = variant;
                if (obj.variant) {
                  const classPrice = prices.find((cp) => cp._id === p.priceId);
                  const installmentValue =
                    (p.installmentStatus && p.installment?.value)
                      ? p.installment.value
                      : (classPrice?.installmentStatus && classPrice?.installment?.value)
                        ? classPrice.installment.value
                        : null;
                  if (installmentValue) obj.numberOfInstallments = installmentValue;
                }
              } else if (p.installmentStatus && p.installment && p.installment.value) {
                obj.numberOfInstallments = p.installment.value;
              }
              return obj;
            });

        const body = {
          formId: formOnlineData._id,
          paymentMethodId: currentPaymentMethod?._id,
          coverFee: convenienceFeeByPayment?.[activePayment] ?? true,
          isMember: isAuthenticated,
          installments,
          products: buildProducts(),
          checkoutDonation: { amount: donationAmount || 0 },
        };

        const res = await FormService.calculateFormPrice(token, body);
        if (!cancelled) {
          const result = res?.result ?? res;
          setCalcResult(result);
          setApiFailed(false);
          onCalcPriceStatusChange && onCalcPriceStatusChange(false);
          onCalcDueAmountChange && onCalcDueAmountChange(result?.dueAmount);
        }
      } catch (e) {
        if (!cancelled) {
          setApiFailed(true);
          onCalcPriceStatusChange && onCalcPriceStatusChange(true);
          enqueueSnackbar("Failed to fetch prices", { variant: "error" });
        }
      } finally {
        if (!cancelled) setFetchingDiscount(false);
      }
    };
    fetchCalcPrice();
    return () => { cancelled = true; };
  }, [totalPaymentWithCVFee, refetchTrigger, donationAmount]);

  useEffect(() => {
    const processedData = processInputData(varients);
    const mergedData = processedData.map((item) => {
      const existing = newProducts?.find((p) => p?.priceId === item?.priceId);
      if (existing) {
        return { ...item, installmentStatus: existing.installmentStatus, installment: existing.installment };
      }
      return item;
    });

    const totalAmount = mergedData?.reduce(
      (sum, obj) => sum + (obj.amount || 0),
      0
    );
    const finalAmount = Number.isNaN(totalAmount) ? 0 : totalAmount;

    dispatch(formSlice.actions.setTotalProducts(finalAmount));
    setProducts(mergedData);
  }, [dispatch, varients]);

  //   const processInputData = (data) => {
  //     const processedProducts =
  //       data && Object.values(data)?.length > 0
  //         ? Object.values(data)?.map((item, index) => {
  //             if (item.variantsInfo && item.variantsInfo.length > 0) {
  //               const totalCounterSum = item.variantsInfo.reduce(
  //                 (sum, obj) => sum + obj.counterValue,
  //                 0,
  //               );
  //               let totalAmount = 0;
  //               if (item.variantsInfo && item.variantsInfo.length > 0) {
  //   item.variantsInfo.forEach((variant) => {
  //     totalAmount += variant.counterValue * (variant.price || variant.amount || 0);
  //   });

  //   return {
  //     title: item.varientTitle,
  //     total: totalCounterSum,
  //     amount: totalAmount,
  //     type: item.type,
  //     priceId: item.id,
  //   };
  // }
  //               // const totalCounterSumAmount = item.variantsInfo.reduce((sum, obj) => sum + obj.amount, 0);
  //               // if (item.variantsInfo && item.variantsInfo.length > 0) {
  //               //   item.variantsInfo.forEach((variant) => {
  //               //     totalAmount += variant.counterValue * variant.amount;
  //               //   });
  //               //   return {
  //               //     title: item.varientTitle,
  //               //     total: totalCounterSum,
  //               //     amount: totalAmount,
  //               //     type: item.type,
  //               //     priceId: item.id,
  //               //   };
  //               // }
  //             } else {
  //               if (item.type === "openAmount") {
  //                 return {
  //                   title: item.varientTitle,
  //                   total: item.counterValue || 0,
  //                   amount: item.price ? item.price * item.counterValue : 0,
  //                   type: item.type,
  //                   priceId: item.id,
  //                 };
  //               } else {
  //                 return {
  //                   title: item.varientTitle,
  //                   total: item.counterValue || 0,
  //                   amount: item.amount * item.counterValue,
  //                   type: item.type,
  //                   priceId: item.id,
  //                 };
  //               }
  //             }
  //           })
  //         : [];
  //     return processedProducts;
  //   };
  const processInputData = data => {
    const processedProducts =
      data && Object.values(data)?.length > 0
        ? Object.values(data)
            ?.map(item => {
              if (item?.variantsInfo && item.variantsInfo.length > 0) {
                const totalCounterSum = item.variantsInfo.reduce(
                  (sum, obj) => sum + obj.counterValue,
                  0
                );

                let totalAmount = 0;

                // item.variantsInfo.forEach((variant) => {
                //   totalAmount +=
                //     variant.counterValue *
                //     (variant.price || variant.amount || 0);
                // });
                item.variantsInfo.forEach(variant => {
                  const qty = variant.counterValue || 0;

                  const price = variant.openAmount
                    ? variant.price || 0
                    : variant.amount || 0;

                  totalAmount += qty * price;
                });
                return {
                  title: item.varientTitle,
                  total: totalCounterSum,
                  amount: totalAmount,
                  type: item.type,
                  priceId: item.id
                };
              }

              
              const priceInfo = formOnlineData?.formPricing?.prices?.find(
                p => p._id === item.id
              );
              const isRequiredCheckbox =
                priceInfo?.isRequired &&
                priceInfo?.limitType === "checkbox" &&
                !priceInfo?.variant;
              const minQty = priceInfo?.isRequired ? (priceInfo?.minimumQuantity || 1) : 0;
              const effectiveCounter = priceInfo?.isRequired
                ? Math.max(item.counterValue || 0, minQty)
                : item.counterValue || 0;
              return {
                title: item.varientTitle,
                total: effectiveCounter,
                amount:
                  item.type === "openAmount"
                    ? (item.price || 0) * effectiveCounter
                    : (item.amount || 0) * effectiveCounter,
                type: item.type,
                priceId: item.id,
                isRequired: priceInfo?.isRequired,
                limitType: priceInfo?.limitType,
                variant: priceInfo?.variant
              };
            })
            .filter(Boolean) 
        : [];

    return processedProducts;
  };
  const calculateInstallmentAmount = (item, amountOverride) => {
    if (!item?.installmentStatus) return "";
    if (item?.installment?.value) {
      const itemAmount = amountOverride !== undefined ? amountOverride : item?.amount;
      return numberFormat({
        number: +itemAmount / item?.installment?.value,
        decimal: 2,
        style: "currency"
      });
    }
    return "";
  };
  const enableProductInstallment = item => {
    const currentPrice = formOnlineData?.formPricing?.prices?.find(
      price => price?._id === item?.priceId
    );
    return currentPrice || {};
  };

  const shouldShowConvenienceFeeToggle =
    currentPaymentMethod?.coverConvenienceFee;
  const isConvenienceFeeOptional =
    currentPaymentMethod?.convenienceFeeInfo?.optional === true;
  console.log("newProducts", newProducts);
  return (
    <>
      {(totalPay?.total !== "0.00" || totalProducts > 0) && (
        <Paper
          sx={{
            minHeight: "278px",
            borderRadius: "10px",
            backgroundColor: theme =>
              theme.palette.mode === "dark" ? "#1A2027" : "#fff"
          }}
        >
          <Wrapper>
            <SummaryTitle>
              <span>Summary</span>
            </SummaryTitle>
            <DataFoundWrapper>
              <PayDetails>
                {formOnlineData?.formPricing?.prices?.map((item, index) => {
                  if (item?.type !== "onRegistration") return null;

                  
                  const registrationVariant = Object.values(
                    varients || {}
                  ).find(v => v.id === item._id);

                  let registrationAmount = item.amount;

                  if (registrationVariant?.variantsInfo?.length) {
                    const selectedVariant = registrationVariant.variantsInfo.find(
                      v => v.counterValue > 0
                    );

                    if (!selectedVariant) return null;
                    registrationAmount = selectedVariant.amount * selectedVariant.counterValue;
                  }
                  return (
                    <SummaryItem>
                      <div>
                        <li key={index}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {item?.title}
                            {item?.isRecurring && (
                              <Tooltip title="Monthly recurring" placement="top">
                                <span style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", padding: "7px 9px", backgroundColor: "rgba(37, 85, 239, 0.13)", borderRadius: "6px" }}>
                                  <RecurringIcon width={9} height={9} />
                                </span>
                              </Tooltip>
                            )}
                          </span>
                          <span>
                            {numberFormat({
                              number: +registrationAmount,
                              decimal: 2,
                              style: "currency"
                            })}
                          </span>
                        </li>
                        <p>{item?.note}</p>
                      </div>
                      {isInstallmentEnabled && item?.isInstallmentTransaction && (
                        <PaymentInstallment
                          onAllowInstallmentChange={e => {
                            const { checked } = e.target;
                            const updatedFormPricing = formOnlineData?.formPricing?.prices?.map(
                              val => {
                                if (val?._id === item?._id) {
                                  return {
                                    ...val,
                                    installmentStatus: checked,
                                    installment: null
                                  };
                                }
                                return val;
                              }
                            );
                            const updatedFormDetails = {
                              ...formOnlineData,
                              formPricing: {
                                ...formOnlineData?.formPricing,
                                prices: updatedFormPricing
                              }
                            };
                            dispatch(
                              formSlice.actions.setFormOnlineData(
                                updatedFormDetails
                              )
                            );
                            setActivePaymentAfter();
                            triggerRefetch();
                          }}
                          installmentValue={item?.installmentStatus}
                          onSelectInstallment={value => {
                            const updatedFormPricing = formOnlineData?.formPricing?.prices?.map(
                              val => {
                                if (val?._id === item?._id) {
                                  return {
                                    ...val,
                                    installment: value
                                  };
                                }
                                return val;
                              }
                            );
                            const updatedFormDetails = {
                              ...formOnlineData,
                              formPricing: {
                                ...formOnlineData?.formPricing,
                                prices: updatedFormPricing
                              }
                            };
                            dispatch(
                              formSlice.actions.setFormOnlineData(
                                updatedFormDetails
                              )
                            );
                            triggerRefetch();
                          }}
                          selectedInstallment={item?.installment}
                          installmentAmount={calculateInstallmentAmount(
                            item,
                            registrationVariant?.variantsInfo?.length
                              ? registrationAmount
                              : undefined
                          )}
                          maxInstallment={
                            item?.installmentDetailsTransaction
                              ?.numberOfInstallment
                          }
                        />
                      )}
                    </SummaryItem>
                  );
                })}
                {newProducts
                  .filter(product => product?.type !== "onRegistration")
                  .map((product, index) => (
                    <>
                      {(product?.total > 0 && product?.amount > 0 ||
                        (product?.isRequired && product?.total > 0)) && (
                        <SummaryItem>
                          <SummaryItemCharge>
                            <SummaryItemInfo>
                              <SummaryItemName>
                                {product?.total} {product?.title}
                              </SummaryItemName>
                            </SummaryItemInfo>
                            {`${numberFormat({
                              number: product?.amount,
                              decimal: 2,
                              style: "currency"
                            })}`}
                          </SummaryItemCharge>
                          {isInstallmentEnabled &&
                            enableProductInstallment(product)
                              ?.isInstallmentTransaction && (
                              <PaymentInstallment
                                onAllowInstallmentChange={e => {
                                  const { checked } = e.target;
                                  const updatedNewProducts = newProducts?.map(
                                    val => {
                                      if (val?.title === product?.title) {
                                        return {
                                          ...val,
                                          installmentStatus: checked,
                                          ...(!checked && { installment: null })
                                        };
                                      }
                                      return val;
                                    }
                                  );
                                  setProducts(updatedNewProducts);
                                  setActivePaymentAfter();
                                  triggerRefetch();
                                }}
                                installmentValue={
                                  product?.installmentStatus || false
                                }
                                onSelectInstallment={value => {
                                  const updatedNewProducts = newProducts?.map(
                                    val => {
                                      if (val?.title === product?.title) {
                                        return {
                                          ...val,
                                          installment: value
                                        };
                                      }
                                      return val;
                                    }
                                  );
                                  setProducts(updatedNewProducts);
                                  triggerRefetch();
                                }}
                                selectedInstallment={product?.installment}
                                installmentAmount={calculateInstallmentAmount(product)}
                                maxInstallment={
                                  enableProductInstallment(product)
                                    ?.installmentDetailsTransaction
                                    ?.numberOfInstallment || 24
                                }
                              />
                            )}
                        </SummaryItem>
                      )}
                    </>
                  ))}
              </PayDetails>
              {!fetchingDiscount && calcResult && +(calcResult.totalDiscount ?? calcResult.discount ?? 0) > 0 && (
                <DiscountRow>
                  <DiscountLabel>Total Discount</DiscountLabel>
                  <DiscountAmount>
                    -{numberFormat({ number: +(calcResult.totalDiscount ?? calcResult.discount), decimal: 2, style: "currency" })}
                  </DiscountAmount>
                </DiscountRow>
              )}
              {/* {(dueAmountCF > 0 && !billMyAccount) && (
              <SummaryItem>
                <SummaryItemCharge>
                  <div>
                    <span style={{ color: '#002e5c', fontSize: '14px' }}>Convenience fee</span>
                  </div>
                  <PaymentAmount>
                    {numberFormat({ number: +dueAmountCF, decimal: 2, style: "currency" })}
                  </PaymentAmount>
                </SummaryItemCharge>
              </SummaryItem>
            )} */}
              {shouldShowConvenienceFeeToggle && !billMyAccount && calcResult && +calcResult.totalAmount > 0 && (
                <SummaryItem>
                  {convenienceFeeByPayment[activePayment] && (
                    <SummaryItemCharge>
                      <div>
                        <span style={{ color: FontColors.Blue002E5C, fontSize: Fonts.GeneralAmountFont.fontSize }}>
                          {" "}
                          Convenience fee
                        </span>
                      </div>
                      <PaymentAmount>
                        {fetchingDiscount ? (
                          <SpinnerIcon $spinning>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.2" strokeLinecap="round">
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                          </SpinnerIcon>
                        ) : numberFormat({
                          number: Math.max(0, +calcResult.convenienceFee),
                          decimal: 2,
                          style: "currency"
                        })}
                      </PaymentAmount>
                    </SummaryItemCharge>
                  )}{" "}
                  {isConvenienceFeeOptional && (
                    <div
                      className={`wrapper check-box__wrapper`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        marginTop: "10px",
                        paddingTop: "10px",
                        color: FontColors.Slate6B7280,
                        borderTop: "1px dashed rgba(0, 46, 92, 0.10)"
                      }}
                    >
                      <label
                        className="check-box-container"
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "18px",
                          height: "18px",
                          flexShrink: 0
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ marginRight: 8 }}
                          checked={convenienceFeeByPayment[activePayment]}
                          onChange={e => {
                            setConvenienceFeeByPayment(prev => ({
                              ...prev,
                              [activePayment]: e.target.checked
                            }));
                          }}
                        />
                        <span className={"checkbox-checkmark"}></span>
                      </label>
                      <span className="text" style={{ lineHeight: "1.4", paddingLeft: "5px" }}>
                        {currentPaymentMethod?.convenienceFeeInfo?.message}
                      </span>
                    </div>
                  )}
                  {/* {convenienceFeeByPayment[activePayment] && (
                    <SummaryItemCharge>
                      <div>
                        <span style={{ color: "#002e5c", fontSize: "14px" }}>
                          {" "}
                          Convenience fee
                        </span>
                      </div>
                      <PaymentAmount>
                        {numberFormat({
                          number: Math.max(0, +dueAmountCF),
                          decimal: 2,
                          style: "currency",
                        })}
                      </PaymentAmount>
                    </SummaryItemCharge>
                  )} */}
                </SummaryItem>
              )}
              <TotalPayment>
                <p>Payment Total</p>
                <span>
                  {fetchingDiscount ? (
                    <SpinnerIcon $spinning>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    </SpinnerIcon>
                  ) : apiFailed ? (
                    <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "4px 10px", cursor: "pointer" }}>↻ Retry</button>
                  ) : calcResult ? (
                    numberFormat({ number: Math.max(0, +calcResult.totalAmount), decimal: 2, style: "currency" })
                  ) : null}
                </span>
              </TotalPayment>
              <TotalDueAmount>
                <p>Due Now</p>
                <span>
                  {fetchingDiscount ? (
                    <SpinnerIcon $spinning>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#002e5c" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    </SpinnerIcon>
                  ) : apiFailed ? (
                    <button onClick={triggerRefetch} style={{ background: "none", border: "1px solid #002e5c", borderRadius: 6, color: FontColors.Blue002E5C, fontSize: Fonts.GeneralSmallButtonFont.fontSize, padding: "4px 10px", cursor: "pointer" }}>↻ Retry</button>
                  ) : calcResult ? (
                    numberFormat({ number: Math.max(0, +calcResult.dueAmount), decimal: 2, style: "currency" })
                  ) : null}
                </span>
              </TotalDueAmount>
            </DataFoundWrapper>
          </Wrapper>
        </Paper>
      )}
    </>
  );
};
